import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import FormTextItem from '../form/FormTextItem';
import { Announcement, EmailMarketingSettings } from '../../model/Stock';
import useAsyncEffect from 'use-async-effect';
import api from '../../api/api'
import { MailChimpSubscriberCountResult } from '../../model/MailChimpSubscriberCountResult';
import TextInput from '../form/TextInput';
import AnimatedSaveButton from '../AnimatedSaveButton';
import { MailChimpApiTestCampaignArgs } from '../../model/MailChimpApiTestCampaignArgs';
import { SaveEmailMarketingSettingsRequest } from '../../model/SaveEmailMarketingSettingsResult';
import AlertEx from '../AlertEx';
import { useSelector } from 'react-redux';
import { IGlobalState } from '../../model/IGlobalState';
import * as Roles from '../../model/Roles';
import { HasAdminRole, IsAdminOrHasRole } from '../../model/Roles';
import { isDirtyJson } from '../../util/util';
import FormSelectItem from '../form/FormSelectItem';
import { EmailMarketingApiMeta } from '../../model/EmailMarketingApiMeta';
import { SelectListItem } from '../../model/SelectListItem';
import { FaSpinner } from 'react-icons/fa';
import { EmailMarketingProviderType } from '../../model/EmailMarketingProviderType';
import { MailerooDnsRecordsResult } from '../../model/MailerooDnsRecordsResult';
import MailerooDnsRecordsModal from './MailerooDnsRecordsModal';
import moment from 'moment';

interface IProps {
    ticker: string;
    apiKey: string;
    stockId: string;

}

export interface IMailChimpMethods {
    save: () => Promise<boolean>
    isDirty: () => boolean
}


const Mailchimp = React.forwardRef<any, IProps>((props: IProps, ref) => {

    const [settings, setSettings] = useState<EmailMarketingSettings>({ mailChimpListId: [] } as any);
    const [apiMeta, setApiMeta] = useState<EmailMarketingApiMeta | undefined>();
    const [subscriberError, setSubscriberError] = useState<string>();
    const [previewUrl, setPreviewUrl] = useState<string>();
    const [loadingMeta, setLoadingMeta] = useState<boolean>(false);
    const user = useSelector((state: IGlobalState) => state.user);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [selectedSendGuid, setSelectedSendGuid] = useState<string>(stock?.announcements?.[0]?.sendGuid || '');
    const baselineSettings = useRef<EmailMarketingSettings | null>(null);
    const [dnsRecords, setDnsRecords] = useState<MailerooDnsRecordsResult | undefined>();
    const [loadingDns, setLoadingDns] = useState<boolean>(false);

    const announcementItems: SelectListItem[] = useMemo(() => {
        if (!stock?.announcements) return [];
        return stock.announcements.slice(0, 10).map((a: Announcement) => ({
            text: `${moment(a.date).format('D-MMM-YY')} - ${a.headline}`,
            value: a.sendGuid!
        }));
    }, [stock?.announcements]);    

    useAsyncEffect(async (isMounted) => {

        const result = await api().getEmailMarketingSettings(props.stockId);
        const loaded = { ...result, mailChimpListId: result.mailChimpListId || [] };
        setSettings(loaded)
        baselineSettings.current = loaded;
    }, [])

    useImperativeHandle(ref, (): IMailChimpMethods => ({

        isDirty() {
            return baselineSettings.current != null && isDirtyJson(settings, baselineSettings.current);
        },

        async save() {

            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const replyTo = (settings.mailChimpReplyTo || '').trim();
            if (replyTo && !emailPattern.test(replyTo)) {
                await AlertEx('From Email must be a valid email address');
                return false;
            }
            if (emailPattern.test((settings.mailChimpFromName || '').trim())) {
                await AlertEx('From Name should be your company name, not an email address');
                return false;
            }

            const request: SaveEmailMarketingSettingsRequest = {
                StockId: props.stockId,
                Settings: settings
            }
            const result = await api().saveEmailMarketingSettings(request);
            if (result.success) {
                const saved = { ...result.settings, mailChimpListId: result.settings.mailChimpListId || [] };
                setSettings(saved)
                baselineSettings.current = saved;
                AlertEx('Saved!');
            } else {
                AlertEx(result.errorMessage);
            }

            return true;

        }

    }));

    const onChange = (e: any) => {
        const s1 = { ...settings } as any;
        s1[e.target.name] = e.target.value;
        // When switching away from CampaignMonitor, trim multi-selection down to the first entry.
        if (e.target.name === 'emailMarketingProviderType'
            && e.target.value !== EmailMarketingProviderType.CampaignMonitor
            && Array.isArray(s1.mailChimpListId) && s1.mailChimpListId.length > 1) {
            s1.mailChimpListId = s1.mailChimpListId.slice(0, 1);
        }
        setSettings(s1);
    }

    const onListIdChange = (e: any) => {
        const target = e.target as HTMLSelectElement;
        const values = target.multiple
            ? Array.from(target.selectedOptions).map(o => o.value).filter(v => v !== '')
            : (target.value ? [target.value] : []);
        setSettings({ ...settings, mailChimpListId: values });
    }

    const sendToSubscribers = async (): Promise<boolean> => {
        const args = sendTestCommon('Send to subscribers');
        if (!args) {
            return false;
        }
        const result = await api().sendToSubscribers(args);
        if (result.errorMessage) {
            alert(result.errorMessage);
        } else {
            alert('Test sent successfully.')
        }
        return true;
    }

    const sendTestEmail = async (): Promise<boolean> => {
        
        const args = sendTestCommon('Send test email');
        if (!args) {
            return false;
        }
        const result = await api().sendTestEmail(args);
        if (result.errorMessage) {
            alert(result.errorMessage);
        } else {
            alert('Campaign created successfully.')
            setPreviewUrl(result.previewUrl);
        }
        return true;
    }

    const sendTestCommon = (type: string): MailChimpApiTestCampaignArgs | undefined => {
        if (!settings) {
            return;
        }
        if (!props.apiKey && settings.mailChimpApiKey) {
            alert('You must define BOTH the investi API Key and the mailchimp key')
            return;
        }
        if (!window.confirm(`${type} for ${props.ticker}. Are you sure?`)) {
            return;
        }
        const args: MailChimpApiTestCampaignArgs = {
            apiKey: props.apiKey,
            listId: settings.mailChimpListId,
            templateId: settings.mailChimpTemplateId,
            replyTo: settings.mailChimpReplyTo || '',
            fromName: settings.mailChimpFromName || '',
            clientId: settings.campaignMonitorClientId || '',
            testEmailAddresses: settings.testEmailAddresses || '',
            announcementSendGuid: selectedSendGuid || undefined,
        }
        return args;
    }

    const loadMeta = async (isMounted: () => boolean) => {
        setSubscriberError('');

        if (settings.mailChimpApiKey) {
            setLoadingMeta(true);
            let result: MailChimpSubscriberCountResult = await api().mailchimpSubscriberCount(settings.mailChimpApiKey, settings.mailChimpListId, settings.campaignMonitorClientId || '', props.stockId, settings.emailMarketingProviderType||'');
            if (isMounted()) {
                if (result.success) {
                    setApiMeta(result.meta)
                } else {
                    setSubscriberError(result.message);
                }
                setLoadingMeta(false);
            }
        }
    }

    useAsyncEffect(async (isMounted) => {

        loadMeta(isMounted)


    }, [settings.mailChimpApiKey, settings.mailChimpListId])



    let listItems: SelectListItem[] = [];
    let templateItems: SelectListItem[] = [];
    let listWarning = "";
    let templateWarning = "";
    const selectedListIds: string[] = settings.mailChimpListId || [];

    if (apiMeta) {
        listItems = [...apiMeta.lists];
        templateItems = [...apiMeta.templates];

        const invalidIds = selectedListIds.filter(id => id && !apiMeta.lists.find(x => x.value === id));
        invalidIds.forEach(id => listItems.push({ text: id, value: id }));
        if (invalidIds.length > 0) {
            listWarning = `${invalidIds.join(', ')} invalid! no list found with that id`;
        }

        if (settings.mailChimpTemplateId && !templateItems.find(x => x.value === settings.mailChimpTemplateId)) {
            templateItems.push({ text: settings.mailChimpTemplateId, value: settings.mailChimpTemplateId });
            templateWarning = `${settings.mailChimpTemplateId} is invalid! no template found with this id)`;
        }
    }

    const createTemplate = async () => {
        const args = sendTestCommon('Create Template');
        if (!args) {
            return false;
        }
        const result = await api().createTemplate(args);
        if (result.errorMessage) {
            alert(result.errorMessage);
        } else {
            alert('Template created successfully.')
            loadMeta(() => true)
            setSettings({
                ...settings,
                mailChimpTemplateId: result.item.value
            })
        }
        return true;
    }

    let mailchimpTemplateUrl = null;
    if (settings.mailChimpApiKey && settings.mailChimpTemplateId) {
        const match = settings.mailChimpApiKey.match(/us(\d+)$/);
        if (match) {
            const region = `us${match[1]}`;
            mailchimpTemplateUrl = `https://${region}.admin.mailchimp.com/templates/design?tid=${settings.mailChimpTemplateId}`;
        }

    }

    const emailMarketingProviderItem:SelectListItem[] =Object.values(EmailMarketingProviderType).map(x=> ({text:x, value: x}));
    const isMaileroo = settings.emailMarketingProviderType === EmailMarketingProviderType.Maileroo;

    // The DNS-records link is only shown once Reply To holds a valid email; the domain is the part after '@'.
    const replyToEmail = (settings.mailChimpReplyTo || '').trim();
    const isValidReplyToEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyToEmail);
    const replyToDomain = isValidReplyToEmail ? replyToEmail.split('@')[1].toLowerCase() : '';
    // The investi-mailer.com.au domain is pre-configured, so its DNS records don't need to be shown to the client.
    const isInvestiMailerDomain = replyToDomain === 'investi-mailer.com.au';

    const showDnsRecords = async () => {
        if (!replyToDomain) return;
        setLoadingDns(true);
        try {
            const result = await api().getMailerooDnsRecords(replyToDomain);
            setDnsRecords(result);
        } catch (e) {
            await AlertEx('Unable to load DNS records. Please try again.');
        } finally {
            setLoadingDns(false);
        }
    };

    return <div className="input-group" >
        
     
        

        <FormSelectItem label="Provider" name='emailMarketingProviderType' value={settings.emailMarketingProviderType} onChange={onChange} items={emailMarketingProviderItem} helperText='The email marketing service provider e.g. Maileroo, Mailchimp or Campaign Monitor'/>
           
        <div className="form-item">
            <div>
                <label htmlFor="">Api Key</label>
                <small style={{ marginLeft: '1rem' }}>Create a new one in MailChimp by clicking on your Avatar (your icon in top right) and then
                    `Account & Billing -&gt; Extras -&gt; API Keys` or in Campaign Monitor Avatar -&gt;  'Account Settings -&gt; API Keys'
                </small>
            </div>


            <TextInput value={settings.mailChimpApiKey} name='mailChimpApiKey' onChange={onChange} />
            
            {apiMeta !== undefined && selectedListIds.length > 0 && <span>Subscriber Count: {apiMeta.numberOfSubscribers}</span>}
            {loadingMeta && <FaSpinner className="fa-spin" size={16} />}
            {subscriberError && <span className="error">{subscriberError}</span>}
            </div>

        { !isMaileroo && <>



        <FormSelectItem
            label={settings.emailMarketingProviderType === EmailMarketingProviderType.CampaignMonitor ? "List Ids" : "List Id"}
            name='mailChimpListId'
            value={settings.emailMarketingProviderType === EmailMarketingProviderType.CampaignMonitor ? selectedListIds : (selectedListIds[0] || '')}
            onChange={onListIdChange}
            items={listItems}
            disabled={!apiMeta}
            helperText={settings.emailMarketingProviderType === EmailMarketingProviderType.CampaignMonitor
                ? [listWarning, 'Hold Ctrl (⌘ on Mac) and click to select multiple lists'].filter(Boolean).join(' — ')
                : listWarning}
            multiple={settings.emailMarketingProviderType === EmailMarketingProviderType.CampaignMonitor}
        />

        <FormSelectItem label="Template Id" name='mailChimpTemplateId' value={settings.mailChimpTemplateId} onChange={onChange} items={templateItems} disabled={!apiMeta} helperText={templateWarning}>
            <div className='spaceBetween' style={{marginTop:'0.5rem'}}>
                {!templateItems.find(x => x.text === 'investi_announcement_template') && <small>Create a default template by clicking <button className="link button-as-link" onClick={createTemplate}>here</button></small>}

                {mailchimpTemplateUrl &&
                    <a target="_blank"
                        href={mailchimpTemplateUrl}
                        rel="noopener noreferrer"
                        className="dashboard-row__website"
                        
                    >[ Open Template in Mailchimp ]</a>
                }
            </div>



        </FormSelectItem>

        { settings.emailMarketingProviderType === EmailMarketingProviderType.Mailchimp &&  <FormTextItem label="Saved Segment Id" name='mailChimpSavedSegmentId' value={settings.mailChimpSavedSegmentId} onChange={onChange} />}
        { settings.emailMarketingProviderType === EmailMarketingProviderType.CampaignMonitor &&  <FormTextItem label="Client Id" name='campaignMonitorClientId' value={settings.campaignMonitorClientId} onChange={onChange} />}
        
        
        </>
        

        }

        

        <FormTextItem label="From Email" name='mailChimpReplyTo' value={settings.mailChimpReplyTo} onChange={onChange}
            helperText={<>
                e.g. info@company.com.au
                { isMaileroo && isValidReplyToEmail && !isInvestiMailerDomain && <>
                    {' — '}
                    <button type="button" className="link button-as-link" style={{ fontSize: 'inherit' }} onClick={showDnsRecords} disabled={loadingDns}>
                        [ Required DNS Records ]
                    </button>
                    { loadingDns && <FaSpinner className="fa-spin" size={12} style={{ marginLeft: '0.4rem' }} /> }
                </> }
            </>}
        />
        <FormTextItem label="From Name" name='mailChimpFromName' value={settings.mailChimpFromName} onChange={onChange} helperText='Your company name' />        
        <FormTextItem label="Test Email Addresses" name='testEmailAddresses' value={settings.testEmailAddresses} onChange={onChange} helperText="Used to send test emails (below)" />

        <hr />

        <div className='send-test-container'>

            {IsAdminOrHasRole(user, props.stockId, Roles.TestSendAnnouncement) && <div className='send-test-item'>
                <p>Send Test Email <small>Creates a Campaign and sends it to "Test Email Addresses" above using the selected announcement.</small></p>

                <div className='form-item'>
                    <select value={selectedSendGuid || (announcementItems.length > 0 ? announcementItems[0].value : '')} name='selectedAnnouncement' onChange={(e: any) => setSelectedSendGuid(e.target.value)}>
                        {announcementItems.map(item => <option key={item.value} value={item.value}>{item.text}</option>)}
                    </select>
                </div>

                <AnimatedSaveButton onClick={sendTestEmail} startingText="Send Test Email" progressText="Creating..." completedText="Done" />

                {previewUrl && <p><a href={previewUrl} target="_blank" rel="noreferrer">Preview Url</a></p>}
            </div>
            }

            {HasAdminRole(user) && <div className='send-test-item'>
                <p>Send To Subscribers <small>The most recent announcement will be used. ListId limited to 5 subscribers</small></p>
                <AnimatedSaveButton onClick={sendToSubscribers} startingText="Send To Subscribers" progressText="Sending..." completedText="Done" />
            </div>}


        </div>

        { dnsRecords && <MailerooDnsRecordsModal result={dnsRecords} onClose={() => setDnsRecords(undefined)} /> }

    </div>
});

export default Mailchimp